import type { FormSection } from "../types/FormSection.js";
import type {
  IncidentFormType,
  IncidentWorkItemUnbranded,
} from "../types/incidentWorkItem.js";
import type { LocationPrecisionOverrideUnbranded } from "../types/locationPrecisionOverride.js";
import {
  createLocationFuzzerAnonymiser,
  isLocationFuzzerAnonymiser,
} from "./util.js";

export type SectionAnonymiser<T> = {
  sectionId: FormSection;
  /**
   * A field named here survives anonymisation, transformed by the function it
   * is given; anything unnamed is dropped. Omission is how a field is kept out
   * of the anonymised copy.
   */
  fieldAnonymisers: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [Property in keyof Partial<T>]: (value: T[Property]) => any;
  };
};

export type FormAnonymiser = {
  formType: IncidentFormType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sectionAnonymisers: SectionAnonymiser<any>[];
};

export type IncidentWorkItemAnonymiser = {
  coreIncidentAnonymiser: (
    incidentWorkItem: IncidentWorkItemUnbranded,
  ) => IncidentWorkItemUnbranded;

  formAnonymisers: FormAnonymiser[];
};

function combineFormAnonymisers(
  formAnonymiser1: FormAnonymiser,
  formAnonymiser2: FormAnonymiser,
) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let sectionAnonymisers: SectionAnonymiser<any>[] = [];

  for (const anon of [
    ...formAnonymiser1.sectionAnonymisers,
    ...formAnonymiser2.sectionAnonymisers,
  ]) {
    const existing = sectionAnonymisers.find(
      (s) => s.sectionId === anon.sectionId,
    );

    if (existing) {
      sectionAnonymisers = [
        ...sectionAnonymisers.filter((s) => s.sectionId !== anon.sectionId),
        {
          sectionId: existing.sectionId,
          fieldAnonymisers: {
            ...existing.fieldAnonymisers,
            ...anon.fieldAnonymisers,
          },
        },
      ];
    } else {
      sectionAnonymisers.push(anon);
    }
  }

  return {
    formType: formAnonymiser1.formType,
    sectionAnonymisers,
  };
}

export function combineIncidentAnonymisers(
  anonymiser1: IncidentWorkItemAnonymiser,
  anonymiser2: IncidentWorkItemAnonymiser,
): IncidentWorkItemAnonymiser {
  let formAnonymisers: FormAnonymiser[] = [];

  for (const anon of [
    ...anonymiser1.formAnonymisers,
    ...anonymiser2.formAnonymisers,
  ]) {
    const existing = formAnonymisers.find((f) => f.formType === anon.formType);

    if (existing) {
      formAnonymisers = [
        ...formAnonymisers.filter((f) => f.formType !== anon.formType),
        combineFormAnonymisers(existing, anon),
      ];
    } else {
      formAnonymisers.push(anon);
    }
  }

  return {
    coreIncidentAnonymiser: (incidentWorkItem) => {
      return anonymiser2.coreIncidentAnonymiser(
        anonymiser1.coreIncidentAnonymiser(incidentWorkItem),
      );
    },
    formAnonymisers,
  };
}

/**
 * Creates a new anonymiser with location precision overrides bound to all location fields.
 * This replaces any field anonymisers that use locationFuzzerAnonymiser with a version
 * that uses the provided precision overrides.
 */
export function bindPrecisionOverridesToAnonymiser(
  anonymiser: IncidentWorkItemAnonymiser,
  precisionOverrides: LocationPrecisionOverrideUnbranded[],
): IncidentWorkItemAnonymiser {
  const boundLocationAnonymiser =
    createLocationFuzzerAnonymiser(precisionOverrides);

  const boundFormAnonymisers = anonymiser.formAnonymisers.map(
    (formAnonymiser) => ({
      ...formAnonymiser,
      sectionAnonymisers: formAnonymiser.sectionAnonymisers.map(
        (sectionAnonymiser) => ({
          ...sectionAnonymiser,
          fieldAnonymisers: Object.fromEntries(
            Object.entries(sectionAnonymiser.fieldAnonymisers).map(
              ([fieldName, fieldAnonymiser]) => {
                // Check if this anonymiser is a location fuzzer using the marker symbol
                if (isLocationFuzzerAnonymiser(fieldAnonymiser)) {
                  return [fieldName, boundLocationAnonymiser];
                }
                return [fieldName, fieldAnonymiser];
              },
            ),
          ),
        }),
      ),
    }),
  );

  return {
    coreIncidentAnonymiser: anonymiser.coreIncidentAnonymiser,
    formAnonymisers: boundFormAnonymisers,
  };
}
